/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Input, Label, Sidebar } from "@nordcloud/gnui";
import { useUpdateApplicationMutation } from "~/generated/graphql";
import { Fieldset, ContactsInput, LoaderWrap } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { ACTIONS, Can } from "~/services/auth";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText, isNotEmpty } from "~/tools";

type FormData = {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  contactIds: string[];
};

type Props = {
  isOpen: boolean;
  appLoading: boolean;
  onClose: () => void;
  defaultValues: Partial<
    Omit<FormData, "contactIds" | "ownerId"> & {
      contacts: { id: string }[];
      owner: { id: string };
    }
  >;
};

export function ApplicationEditSidebar({
  isOpen,
  onClose,
  appLoading,
  defaultValues,
}: Props) {
  const [ownerId, setOwnerId] = useState<string[]>([""]);
  const [contactIds, setContactIds] = useState<string>("");

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,

    formState: { errors },
  } = useForm<FormData>();

  const [updateApplication, { loading: isUpdateApplicationLoading }] =
    useUpdateApplicationMutation();

  const onUpdate = async (values: FormData) => {
    const { id, ...input } = values;

    try {
      await updateApplication({
        variables: {
          nid: id,
          input: {
            ...input,
            displayName: input.name,
          },
        },
      });

      onClose();
      showSuccess(generateActionSuccessText(`Application`)()("updated")());
    } catch (error) {
      showError(error.message, {
        autoClose: false,
      });
    }
  };

  useEffect(() => {
    register("ownerId");
    register("contactIds");
  }, [register]);

  useEffect(() => {
    setOwnerId([defaultValues?.owner?.id ?? ""]);
    setValue("ownerId", defaultValues?.owner?.id ?? "");
    const contactId = defaultValues?.contacts?.[0]?.id ?? "";
    setContactIds(contactId);
    setValue("contactIds", isNotEmpty(contactId) ? [contactId] : []);
    setValue("name", defaultValues.name ?? "");
    setValue("description", defaultValues.description ?? "");
    setValue("id", defaultValues.id ?? "");
  }, [defaultValues, setValue]);

  const isLoading = appLoading || isUpdateApplicationLoading;

  return (
    <Sidebar
      isOpen={isOpen}
      title="Edit Application"
      footer={
        <Button
          disabled={isLoading}
          margin="1rem"
          type="submit"
          form="updateApplicationForm"
        >
          Update Application
        </Button>
      }
      onClick={onClose}
    >
      <LoaderWrap inContent loading={isLoading}>
        <form id="updateApplicationForm" onSubmit={handleSubmit(onUpdate)}>
          <input type="hidden" {...register("id")} />
          <Fieldset>
            <Label name="Application Name" htmlFor="input-name" />
            <Input
              id="input-name"
              type="text"
              {...register("name")}
              placeholder="Application Name"
              defaultValue={defaultValues?.name ?? ""}
            />
          </Fieldset>
          <Can action={ACTIONS.updateApplicationOwner}>
            <Fieldset>
              <Label required name="Application Owner" />
              <ContactsInput
                placeholder="Select contact person"
                value={[ownerId.toString()]}
                status={errors.ownerId && "error"}
                onChange={(contactId: string) => {
                  try {
                    setOwnerId([contactId]);
                    clearErrors("ownerId");
                    setValue("ownerId", contactId);
                    showSuccess(SUCCESS_TEXT.ownerAdded);
                  } catch {
                    showError(ERROR_TEXT.default);
                  }
                }}
              />
            </Fieldset>
          </Can>
          <Fieldset>
            <Label name="Contact Person" />
            <ContactsInput
              placeholder="Select contact person"
              value={[contactIds]}
              status={errors.contactIds && "error"}
              onChange={(index: string) => {
                try {
                  setContactIds(index);
                  setValue("contactIds", [index]);
                  clearErrors("contactIds");
                  showSuccess(SUCCESS_TEXT.contactPersonAdded);
                } catch {
                  showError(ERROR_TEXT.default);
                }
              }}
            />
          </Fieldset>
          <Fieldset>
            <Label name="Description" />
            <Input
              type="text"
              {...register("description")}
              placeholder="Application Description"
              defaultValue={defaultValues?.description ?? ""}
            />
          </Fieldset>
        </form>
      </LoaderWrap>
    </Sidebar>
  );
}
